/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { queryClient } from '@/app/queryClient'
import { useTranslation } from '@/app/translations/translations-hooks'
import ecomPolicyResources from '@/modules/ecom-policy/resources'
import type { ProductExtended } from '@/modules/ecom-policy/types'

import queries from '../queries'

import { getGroupedDataList } from './helpers'
import { usePolicyFilters } from './Policy.context'

export const usePolicyId = () => {
  const translate = useTranslation()
  const params = useParams()
  const ecomPolicyId = Number(params.ecomPolicyId)

  if (isNaN(ecomPolicyId)) throw Error(translate('Policy ID is not a number!'))
  return ecomPolicyId
}

export const useUpdateProduct = () => {
  const policyId = usePolicyId()

  const { mutate, isLoading } = useMutation(ecomPolicyResources.postPolicyProducts, {
    async onMutate(updateProductPayload) {
      const { isSelected, isMandatory, productId, quantityLimit, alias } =
        updateProductPayload.payload.products[0]
      const queryKey = queries.policyProducts(policyId)

      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(queryKey)

      // Snapshot the previous value
      const previousPolicyProducts = queryClient.getQueryData<{
        products: ProductExtended[]
        groupNames: string[]
        accessoriesByProductId: Record<number, number[]> | undefined
      }>(queryKey)
      const updatedProducts =
        previousPolicyProducts?.products.map(product =>
          product.id === productId
            ? { ...product, isSelected, isMandatory, quantityLimit, alias: alias || product.name }
            : product
        ) || []
      const updatedValues = {
        ...previousPolicyProducts,
        products: updatedProducts,
      }

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, updatedValues)

      // Return a context with the previous values
      return previousPolicyProducts
    },
    // If the mutation fails, use the context we returned above
    onError(_err, _selectProductPayload, previousPolicyProducts) {
      queryClient.setQueryData(queries.policyProducts(policyId), previousPolicyProducts)
      queryClient.invalidateQueries(queries.policyProducts(policyId))
    },
  })

  return { updateProduct: mutate, isUpdating: isLoading }
}

export const useEverSelectedPolicyCategories = ({
  selectedCategoryIds,
  productsWithVisibleAccessoriesIds,
}: {
  selectedCategoryIds: number[] | null
  productsWithVisibleAccessoriesIds: number[] | null
}): [number[] | null, (v: number[] | null) => void] => {
  const [everSelectedCategoryIds, setEverSelectedCategoryIds] = useState<number[] | null>(null)

  useEffect(() => {
    if (!selectedCategoryIds) return

    selectedCategoryIds?.forEach(id => {
      if (!everSelectedCategoryIds) {
        setEverSelectedCategoryIds([id])
        return
      }

      if (!everSelectedCategoryIds.includes(id)) {
        setEverSelectedCategoryIds([...everSelectedCategoryIds, id])
      }
    })
  }, [selectedCategoryIds, everSelectedCategoryIds])

  useEffect(() => {
    // Reset on parent product change in the accessories mode and on mode change
    setEverSelectedCategoryIds(null)
  }, [productsWithVisibleAccessoriesIds])

  return [everSelectedCategoryIds, setEverSelectedCategoryIds]
}

export const useGroupedProductList = () => {
  const policyId = usePolicyId()
  const filters = usePolicyFilters()
  const [selectedCategoryIds] = filters.selectedCategoryIds
  const [stockFilter] = filters.stockFilter
  const [searchQuery] = filters.searchQuery
  const [onlySelectedFilter] = filters.onlySelectedFilter
  const [onlyMandatoryFilter] = filters.onlyMandatoryFilter
  const { productsWithVisibleAccessoriesIds } = filters
  const [everSelectedCategoryIds] = useEverSelectedPolicyCategories({
    selectedCategoryIds,
    productsWithVisibleAccessoriesIds,
  })

  const { data, dataUpdatedAt } = useQuery(queries.policyProducts(policyId), () =>
    ecomPolicyResources.getPolicyProducts(policyId)
  )

  const list = useMemo(
    () =>
      getGroupedDataList({
        priceList: data?.products,
        groupNames: data?.groupNames,
        selectedCategoryIds,
        stockFilter,
        searchQuery,
        onlySelectedFilter,
        onlyMandatoryFilter,
        everSelectedCategoryIds,
        productsWithVisibleAccessoriesIds,
        accessoriesByProductId: data?.accessoriesByProductId || null,
      }),
    [
      dataUpdatedAt,
      selectedCategoryIds?.length,
      stockFilter,
      searchQuery,
      onlySelectedFilter,
      onlyMandatoryFilter,
      everSelectedCategoryIds?.length,
      productsWithVisibleAccessoriesIds?.[0],
    ]
  )

  return list
}
